import { useState } from "react";
import { Mail, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { useContent, API } from "@/context/ContentContext";
import { waLink } from "@/utils/scroll";

const inputCls =
  "w-full rounded-xl border border-slate-900/10 bg-surface px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-500 transition-colors focus:border-electric/60 focus:outline-none focus:ring-1 focus:ring-electric/40";

export default function Contact() {
  const { content } = useContent();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  if (!content) return <section id="contact" className="py-16 md:py-28" />;
  const s = content.settings;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios_post();
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const axios_post = async () => {
    const axios = (await import("axios")).default;
    await axios.post(`${API}/contact`, form);
  };

  return (
    <section id="contact" className="py-16 md:py-28" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          overline="Contact"
          title="Talk to us anytime"
          sub="Questions about plans, setup or your order? Our team replies fast — day or night."
        />
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="glass h-full rounded-3xl p-8" data-testid="contact-info-card">
              <h3 className="font-display text-xl font-bold text-slate-900">We're here 24/7</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-500">
                The fastest way to reach us is WhatsApp — most messages get a reply within minutes.
              </p>
              <div className="mt-8 space-y-4">
                <a
                  href={waLink(s.whatsapp_intl, "Hello IPTV HUB, I have a question...")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="flex items-center justify-between rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-6 py-5 transition-all hover:bg-emerald-500/20 hover:shadow-[0_0_28px_rgba(37,211,102,0.25)]"
                >
                  <span className="flex items-center gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#25D366] text-slate-900">
                      <WhatsAppIcon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block text-xs text-slate-500">WhatsApp — instant chat</span>
                      <span className="block font-display text-base font-bold text-slate-900">{s.whatsapp_display}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-emerald-300">
                    <Clock className="h-3.5 w-3.5" /> 24/7
                  </span>
                </a>
                <a
                  href={`mailto:${s.email}`}
                  data-testid="contact-email-button"
                  className="flex items-center gap-3 rounded-2xl border border-slate-900/10 bg-slate-900/[0.03] px-6 py-5 transition-all hover:border-electric/40 hover:shadow-[0_0_24px_rgba(0,240,255,0.15)]"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-electric/25 bg-electric/10 text-electric">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs text-slate-500">Email us</span>
                    <span className="block font-display text-base font-bold text-slate-900">{s.email}</span>
                  </span>
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="glass h-full rounded-3xl p-8" data-testid="contact-form">
              <h3 className="font-display text-xl font-bold text-slate-900">Send a message</h3>
              <div className="mt-6 space-y-4">
                <input
                  data-testid="contact-name-input"
                  className={inputCls}
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  data-testid="contact-email-input"
                  type="email"
                  className={inputCls}
                  placeholder="Your email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <textarea
                  data-testid="contact-message-input"
                  rows={5}
                  className={`${inputCls} resize-none`}
                  placeholder="Your message..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-send-button"
                  className="btn-primary inline-flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base disabled:opacity-60"
                >
                  <Send className="h-4 w-4" /> {sending ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
